import posthog from 'posthog-js'

interface UserIdentification {
  id: number
  email: string
  name?: string
  created_at?: string
  trial_ends_at?: Date | string | null
  free_account?: boolean
  on_trial?: boolean
  current_billing_plan?: string
  stripe_status?: string
  active_subscription?: {
    stripe_price?: string
    stripe_status?: string
  } | null
  // Additional analytics properties
  is_agency?: boolean
  first_promoter_ref_id?: string | null
  owned_teams?: Array<{ pid: number | string }> | null
}

interface TeamGroup {
  pid: number | string
  name: string
  current_billing_plan?: string
}

export function usePostHog() {
  posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
    api_host: 'https://us.i.posthog.com',
    defaults: '2025-11-30',
    person_profiles: 'identified_only',
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true
      }
    }
  })

  /**
   * Identify the current user to PostHog.
   * Uses email as distinct_id for cross-platform identity merging with marketing site.
   * This allows correlating webapp users with tag.js visitors when they share an email.
   */
  const identifyUser = (user: UserIdentification) => {
    const trialEndsAt = user.trial_ends_at
      ? user.trial_ends_at instanceof Date
        ? user.trial_ends_at.toISOString()
        : user.trial_ends_at
      : null

    posthog.identify(user.email, {
      user_id: user.id,
      name: user.name,
      created_at: user.created_at,
      trial_ends_at: trialEndsAt,
      free_account: user.free_account,
      on_trial: user.on_trial,
      current_billing_plan: user.current_billing_plan,
      stripe_status: user.stripe_status,
      plan: user.active_subscription?.stripe_price,
      // Additional analytics properties
      is_agency: user.is_agency,
      first_promoter_ref_id: user.first_promoter_ref_id,
      owned_teams_count: user.owned_teams?.length ?? 0
    })
  }

  /**
   * Set the current team context as a PostHog Group.
   * Enables team-level analytics for users who belong to multiple teams.
   */
  const setTeamGroup = (team: TeamGroup) => {
    posthog.group('team', String(team.pid), {
      name: team.name,
      plan: team.current_billing_plan
    })
  }

  /**
   * Reset PostHog identity on logout.
   * Prevents user merging when multiple people use the same device.
   */
  const resetUser = () => {
    posthog.reset()
  }

  /**
   * Capture a custom event with optional properties.
   */
  const capture = (event: string, properties?: Record<string, unknown>) => {
    posthog.capture(event, properties)
  }

  return { posthog, identifyUser, setTeamGroup, resetUser, capture }
}
